import { useState, useRef, useCallback, useEffect } from 'react';
import { Loader2, MailCheck, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { classifySignUpError, logSignUpError } from '@/utils/authErrors';
import { cn } from '@/lib/utils';

interface EmailVerificationStepProps {
  email: string;
  onVerified: () => void;
}

const CODE_LENGTH = 6;

/**
 * GoTrue enforces a minimum interval between auth emails per address (~60s),
 * independent of the per-hour send limit. Without this cooldown a second tap on
 * "resend" is guaranteed to come back as 429 over_email_send_rate_limit.
 */
const RESEND_COOLDOWN_SECONDS = 60;

export const EmailVerificationStep = ({ email, onVerified }: EmailVerificationStepProps) => {
  const { t } = useI18n();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const isCoolingDown = cooldownSeconds > 0;

  // Only ticks while a cooldown is actually running, so the first render (a page
  // reload onto this screen) leaves the button enabled.
  useEffect(() => {
    if (!isCoolingDown) return;
    const interval = setInterval(() => {
      setCooldownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isCoolingDown]);

  const code = digits.join('');
  const isComplete = code.length === CODE_LENGTH && digits.every(d => d !== '');

  const focusAt = (index: number) => {
    inputRefs.current[Math.max(0, Math.min(index, CODE_LENGTH - 1))]?.focus();
  };

  const handleChange = useCallback((index: number, value: string) => {
    if (value.length > 1) {
      const cleaned = value.replace(/\D/g, '').slice(0, CODE_LENGTH);
      const next = Array(CODE_LENGTH).fill('');
      cleaned.split('').forEach((ch, i) => { next[i] = ch; });
      setDigits(next);
      focusAt(cleaned.length - 1);
      return;
    }

    const digit = value.replace(/\D/g, '');
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    setError('');

    if (digit && index < CODE_LENGTH - 1) {
      focusAt(index + 1);
    }
  }, [digits]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusAt(index - 1);
    }
  }, [digits]);

  const handleVerify = async () => {
    if (!isComplete) return;
    setLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: code,
        type: 'signup',
      });
      if (error) {
        setError(t('auth.email_verification.invalid_code'));
        setDigits(Array(CODE_LENGTH).fill(''));
        focusAt(0);
      } else {
        onVerified();
      }
    } catch {
      setError(t('auth.email_verification.verify_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      // resend() resolves with { error } instead of throwing — the catch below
      // only ever sees a transport failure, so a server error has to be read off
      // the result or the UI reports a success that never happened.
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
      });

      if (resendError) {
        const { reason } = classifySignUpError(resendError);
        logSignUpError(resendError, reason);

        if (reason === 'email_send_failure') {
          // The server already refused; retrying inside the window earns another 429.
          setError(t('auth.email_verification.resend_rate_limited'));
          setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
        } else {
          setError(t('auth.email_verification.resend_error'));
        }
        return;
      }

      setResent(true);
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      setTimeout(() => setResent(false), 5000);
    } catch {
      setError(t('auth.email_verification.resend_error'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-[calc(100dvh-140px)]">
      <div className="flex-1 flex flex-col justify-center space-y-6 py-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-sossiety-red-surface border border-sossiety-red-border flex items-center justify-center">
            <MailCheck className="w-6 h-6 text-sossiety-red" />
          </div>
          <div className="space-y-1">
            <h3 className="text-white font-semibold text-base">{t('auth.email_verification.title')}</h3>
            <p className="text-white/50 text-sm leading-relaxed">
              {t('auth.email_verification.description')}
              <br />
              <span className="text-white/80 font-medium">{email}</span>
            </p>
          </div>
        </div>

        <div className="flex justify-center gap-2">
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={el => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              onFocus={e => e.target.select()}
              className={cn(
                'w-11 h-14 rounded-xl border text-center text-xl font-bold text-white bg-sossiety-input',
                'focus:outline-none focus:border-sossiety-red transition-colors',
                error ? 'border-red-500' : 'border-[rgba(255,255,255,0.1)]',
                digit && !error && 'border-white/30'
              )}
            />
          ))}
        </div>

        {error && (
          <p className="text-center text-xs text-red-300">{error}</p>
        )}

        {/* Verification emails can land in spam; a quiet nudge for the few who don't see it. */}
        <p className="text-center text-xs text-white/40">{t('auth.email_verification.spam_hint')}</p>
      </div>

      <div className="sticky bottom-0 z-10 mt-auto space-y-3 bg-sossiety-dark pt-4 pb-2 border-t border-white/5">
        <Button
          type="button"
          variant="primary"
          size="lg"
          disabled={!isComplete || loading}
          onClick={handleVerify}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('auth.email_verification.verifying')}
            </>
          ) : (
            t('auth.email_verification.confirm')
          )}
        </Button>

        <div className="text-center space-y-2">
          {resent && (
            <p className="text-xs text-sossiety-success">{t('auth.email_verification.resent')}</p>
          )}
          <button
            type="button"
            disabled={resending || isCoolingDown}
            onClick={handleResend}
            className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1.5 mx-auto disabled:cursor-not-allowed disabled:hover:text-white/40"
          >
            {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {isCoolingDown
              ? t('auth.email_verification.resend_cooldown', { seconds: cooldownSeconds })
              : t('auth.email_verification.resend')}
          </button>
        </div>
      </div>
    </div>
  );
};
